import { useState, useCallback } from "react";
import { Star } from "lucide-react";
import { openTelegramLink } from "@telegram-apps/sdk";
import { EXTERNAL_URLS } from "@shared/configs/externalUrls";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./dialog";
import { Button } from "./button";
import { Input } from "./input";
import { apiClients } from "@infrastructure/clients";
import { catchError } from "@shared/utils/errors/catchError";
import { toast } from "sonner";
import styles from "./orderFeedbackModal.module.scss";

interface OrderFeedbackModalProps {
  orderNumber: string;
  isOpen: boolean;
  onClose: () => void;
}

export function OrderFeedbackModal({ orderNumber, isOpen, onClose }: OrderFeedbackModalProps) {
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [comment, setComment] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStarClick = useCallback((value: number) => {
    setRating(value);
  }, []);

  const handleStarHover = useCallback((value: number) => {
    setHoveredRating(value);
  }, []);

  const handleStarLeave = useCallback(() => {
    setHoveredRating(0);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (rating === 0) {
      toast.error("Пожалуйста, выберите оценку");
      return;
    }

    setIsSubmitting(true);
    const [error] = await catchError(
      apiClients.http.feedback.createFeedback(orderNumber, {
        rating,
        comment: comment.trim() || null,
      })
    );

    setIsSubmitting(false);

    if (error) {
      // Проверяем, не был ли отзыв уже оставлен
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes("уже существует") || errorMessage.includes("already exists")) {
        toast.info("Отзыв для этого заказа уже был оставлен");
      } else {
        toast.error("Не удалось отправить отзыв. Попробуйте позже.");
      }
      onClose();
      return;
    }

    toast.success("Спасибо за ваш отзыв!");
    onClose();
    // Сбрасываем форму
    setRating(0);
    setComment("");
  }, [rating, comment, orderNumber, onClose]);

  const handleSupportClick = useCallback(() => {
    openTelegramLink(EXTERNAL_URLS.BOT);
  }, []);

  const displayRating = hoveredRating || rating;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isSubmitting && onClose()}>
      <DialogContent className={styles.content} disableClose={isSubmitting}>
        <DialogHeader>
          <DialogTitle className={styles.title}>Оцените заказ</DialogTitle>
          <DialogDescription className={styles.description}>
            Поделитесь вашим мнением о заказе {orderNumber}
          </DialogDescription>
        </DialogHeader>

        <div className={styles.ratingSection}>
          <div className={styles.stars}>
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                className={styles.starButton}
                onClick={() => handleStarClick(value)}
                onMouseEnter={() => handleStarHover(value)}
                onMouseLeave={handleStarLeave}
                aria-label={`Оценка ${value} из 5`}
              >
                <Star
                  className={`${styles.star} ${
                    value <= displayRating ? styles.starFilled : styles.starEmpty
                  }`}
                  size={32}
                  fill={value <= displayRating ? "currentColor" : "none"}
                />
              </button>
            ))}
          </div>
        </div>

        <div className={styles.commentSection}>
          <textarea
            placeholder="Оставьте комментарий (необязательно)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className={styles.commentInput}
            maxLength={500}
            rows={4}
          />
        </div>

        <div className={styles.actions}>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || rating === 0}
            className={styles.submitButton}
          >
            {isSubmitting ? "Отправка..." : "Отправить отзыв"}
          </Button>
          <Button
            variant="secondary"
            onClick={handleSupportClick}
            className={styles.supportButton}
          >
            Связаться с поддержкой
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
